/**
 * ErrorBoundary genérico para envolver el árbol de las 3 SPAs (equipo,
 * dashboard, landing). Captura errores de render, muestra un fallback
 * sobrio y permite "reintentar" (clear error state) o "recargar" (full
 * page reload).
 *
 * Diseño:
 *   - Sin dependencia dura a Sentry. Acepta `onError` para hookear con
 *     `captureError` de @aosenuma/lib-supabase u otro logger; si falta,
 *     solo loguea a console.error. Esto deja la UI estable aunque Sentry
 *     no esté configurado (ej. dev sin DSN o build de preview).
 *   - Estilos inline + clases con prefijo `error-boundary-*` para no
 *     depender de tokens.css (si tokens.css falló al cargar también
 *     debería rendir bien). Usa colores hardcoded sobrios.
 *   - No oculta el error original en consola — el técnico puede abrir
 *     devtools y ver el stack completo. El fallback solo enseña el
 *     `error.message` al usuario final.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Identificador de la app — se incluye en el reporter y en el badge del fallback. */
  app?: 'landing' | 'dashboard' | 'equipo';
  /** Reporter para integrar con captureError u otro logger. Llamado con el
   *  error y el componentStack. Los errores que el reporter mismo lance se
   *  silencian (no provocan un loop). */
  onError?: (err: Error, info: ErrorInfo) => void | Promise<void>;
  /** Fallback custom: nodo o función que recibe `{ error, reset }`. */
  fallback?: ReactNode | ((args: { error: Error; reset: () => void }) => ReactNode);
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
    if (this.props.onError) {
      try {
        const r = this.props.onError(error, info);
        if (r instanceof Promise) r.catch(() => { /* ignore reporter failure */ });
      } catch {
        /* ignore reporter failure */
      }
    }
  }

  reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const fb = this.props.fallback;
    if (fb !== undefined) {
      return typeof fb === 'function' ? fb({ error, reset: this.reset }) : fb;
    }
    return <DefaultFallback error={error} reset={this.reset} app={this.props.app} />;
  }
}

function DefaultFallback({
  error,
  reset,
  app,
}: {
  error: Error;
  reset: () => void;
  app?: string;
}): ReactNode {
  return (
    <div role="alert" className="error-boundary-root">
      <div className="error-boundary-card">
        <div className="error-boundary-kicker">
          {app ? `${app} · error inesperado` : 'error inesperado'}
        </div>
        <h2 className="error-boundary-title">Algo se rompió en pantalla.</h2>
        <p className="error-boundary-msg">
          {error.message || 'Error sin mensaje.'}
        </p>
        <div className="error-boundary-actions">
          <button type="button" className="error-boundary-btn error-boundary-btn-ghost" onClick={reset}>
            Reintentar
          </button>
          <button
            type="button"
            className="error-boundary-btn error-boundary-btn-primary"
            onClick={() => {
              if (typeof window !== 'undefined') window.location.reload();
            }}
          >
            Recargar
          </button>
        </div>
        <p className="error-boundary-hint">
          Si el problema persiste, recarga la pestaña o avísale al equipo aosenuma.
        </p>
      </div>
    </div>
  );
}
